import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, FolderOpen } from "lucide-react";
import MaterialCard from "./MaterialCard";

export function groupByFolder(materials) {
  const map = {};
  for (const m of materials) {
    if (m.folder_id) (map[m.folder_id] = map[m.folder_id] || []).push(m);
  }
  return map;
}

// Raccolte e materiali senza cartella in un'unica lista, dal più recente.
export function mixItems(folders, materials, byFolder, hideEmpty = false) {
  const fs = hideEmpty ? folders.filter((f) => (byFolder[f.id] || []).length > 0) : folders;
  return [
    ...fs.map((f) => ({ key: `f-${f.id}`, folder: f, date: f.created_at })),
    ...materials.filter((m) => !m.folder_id).map((m) => ({ key: m.id, m, date: m.created_at })),
  ].sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
}

export default function MaterialFolderGrid({ folders, materials, emptyText, hideEmptyFolders = false }) {
  const [params, setParams] = useSearchParams();
  const openId = params.get("cartella") || "";
  const byFolder = useMemo(() => groupByFolder(materials), [materials]);
  const openFolder = folders.find((f) => f.id === openId);

  const setOpen = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set("cartella", id);
    else next.delete("cartella");
    setParams(next);
  };

  if (openFolder) {
    const list = byFolder[openFolder.id] || [];
    return (
      <div className="space-y-3" data-testid="folder-open-view">
        <button
          type="button"
          onClick={() => setOpen("")}
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#0153A3] hover:underline"
          data-testid="folder-back-button"
        >
          <ChevronLeft size={16} /> Torna indietro
        </button>
        <div className="flex items-center gap-2 text-[#1F2937]">
          <FolderOpen size={18} className="text-[#0153A3]" />
          <h3 className="text-base font-semibold" data-testid="folder-open-title">{openFolder.name}</h3>
        </div>
        {list.length === 0 ? <Empty text="Nessun materiale disponibile." /> : <Grid items={list} />}
      </div>
    );
  }

  const items = mixItems(folders, materials, byFolder, hideEmptyFolders);
  if (items.length === 0) return <Empty text={emptyText} />;
  return <MixedGrid items={items} byFolder={byFolder} onOpen={setOpen} testId="material-folder-grid" />;
}

export function MixedGrid({ items, byFolder, onOpen, folderLink, testId }) {
  let fi = 0;
  let mi = 0;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid={testId}>
      {items.map((it) =>
        it.folder ? (
          <MaterialCard
            key={it.key}
            folder={it.folder}
            count={(byFolder[it.folder.id] || []).length}
            index={fi++}
            to={folderLink ? folderLink(it.folder) : undefined}
            onClick={onOpen ? () => onOpen(it.folder.id) : undefined}
          />
        ) : (
          <MaterialCard key={it.key} m={it.m} index={mi++} />
        )
      )}
    </div>
  );
}

function Grid({ items }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((m, i) => <MaterialCard key={m.id} m={m} index={i} />)}
    </div>
  );
}

function Empty({ text }) {
  return (
    <div className="rounded-xl border border-dashed border-[#E5E7EB] bg-[#F7F9FC] p-8 text-center text-sm text-[#6B7280]" data-testid="material-folder-grid-empty">
      {text}
    </div>
  );
}
